import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { CheckCircle, Circle, Lightning, ArrowRight, Rocket, Trophy, DownloadSimple, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { ChampionCameo } from "../components/ChampionCameo";

const fireWeekOneConfetti = () => {
  confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 } });
  setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { x: 0.2, y: 0.5 } }), 350);
  setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { x: 0.8, y: 0.5 } }), 700);
};

export default function PlaybookPage() {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await client.get("/playbook/status");
      setData(r.data);
      if (r.data.completed === r.data.total && r.data.total > 0
          && !localStorage.getItem("fp_week1_confetti")) {
        localStorage.setItem("fp_week1_confetti", "1");
        fireWeekOneConfetti();
        toast.success("WEEK ONE COMPLETE — you're officially rolling");
      }
    } catch { /* page shows loading state */ }
  };

  const downloadBadge = async () => {
    try {
      const r = await client.get("/playbook/badge.png", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "freightpilot-week-one-badge.png";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Badge not ready yet"); }
  };

  const shareWin = async () => {
    const text = "Week one on FreightPilot AI ✅ — dispatch, invoicing and payroll now run themselves. If you run trucks, look at freightpilotai.ai";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Week One Complete", text, url: "https://freightpilotai.ai" });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Brag copied — paste it anywhere");
      }
    } catch { /* user cancelled share sheet */ }
  };

  useEffect(() => {
    try { sessionStorage.removeItem("fp_fresh_signup"); } catch { /* noop */ }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (step) => {
    if (step.auto) return;
    try {
      await client.post("/playbook/check", { key: step.key, done: !step.done });
      await load();
      if (!step.done) toast.success("Checked off — keep rolling");
    } catch { toast.error("Could not save that"); }
  };

  if (!data) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="playbook-loading">Loading your playbook…</div>;
  }

  const allDone = data.completed === data.total;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="playbook-page">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-emerald-600 mb-1">
          <Rocket size={14} weight="fill" /> First Week Playbook
        </div>
        <h1 className="text-2xl font-black leading-tight" data-testid="playbook-title">
          Your first 7 days, mapped out
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
          One small win a day. By day 7 your dispatch, invoicing, and payroll run themselves —
          the same setup fleets use to get paid faster from week one.
        </p>
      </div>

      <div className="rounded-xl border bg-card p-4" data-testid="playbook-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Week progress</span>
          <span className="text-sm font-black" data-testid="playbook-progress-count">
            {data.completed}/{data.total} · {data.progress_pct}%
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${data.progress_pct}%` }}
          />
        </div>
        {allDone && (
          <div className="mt-2 text-sm font-semibold text-emerald-600" data-testid="playbook-all-done">
            Week one complete — you're officially running this thing. 🏆
          </div>
        )}
      </div>

      {allDone && (
        <div className="rounded-xl border-2 border-yellow-400/60 bg-gradient-to-br from-yellow-500/10 via-amber-500/5 to-transparent p-5"
             data-testid="playbook-badge-card">
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-yellow-400 text-black shrink-0">
              <Trophy size={26} weight="fill" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-black text-lg leading-tight">Week One Champion</div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Most fleets never set this up. You did it in a week — grab your badge and let other truckers know.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button type="button" onClick={downloadBadge} data-testid="badge-download-btn"
                        className="inline-flex items-center gap-1.5 rounded-md bg-yellow-400 hover:bg-yellow-300 text-black px-3.5 py-2 text-sm font-bold transition-colors">
                  <DownloadSimple size={15} weight="bold" /> Download badge
                </button>
                <button type="button" onClick={shareWin} data-testid="badge-share-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border px-3.5 py-2 text-sm font-bold hover:bg-muted transition-colors">
                  <ShareNetwork size={15} weight="bold" /> Share the win
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {allDone && <ChampionCameo />}

      <div className="space-y-4">
        {data.days.map((d) => {
          const dayDone = d.steps.every((s) => s.done);
          return (
            <div
              key={d.day}
              className={`rounded-xl border bg-card p-4 ${dayDone ? "border-emerald-500/40" : ""}`}
              data-testid={`playbook-day-${d.day}`}
            >
              <div className="flex items-center gap-3 mb-1">
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-sm font-black shrink-0 ${
                  dayDone ? "bg-emerald-500 text-white" : "bg-muted text-foreground"
                }`}>
                  {dayDone ? <CheckCircle size={18} weight="fill" /> : `D${d.day}`}
                </span>
                <div className="min-w-0">
                  <div className="font-bold leading-tight">{d.title}</div>
                  {d.why && <div className="text-xs text-muted-foreground">{d.why}</div>}
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {d.steps.map((s) => (
                  <div
                    key={s.key}
                    className="flex items-start gap-2.5 rounded-lg border bg-background px-3 py-2.5"
                    data-testid={`playbook-step-${s.key}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(s)}
                      disabled={s.auto}
                      data-testid={`playbook-check-${s.key}`}
                      title={s.auto ? "Auto-detected — completes itself when you do it" : "Tap to check off"}
                      className={`mt-0.5 shrink-0 ${s.done ? "text-emerald-500" : "text-muted-foreground"} ${s.auto ? "cursor-default" : "hover:text-emerald-600"}`}
                    >
                      {s.done ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-semibold leading-tight ${s.done ? "line-through text-muted-foreground" : ""}`}>
                        {s.label}
                        {s.auto && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase font-bold text-sky-600 align-middle">
                            <Lightning size={9} weight="fill" /> auto
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{s.hint}</div>
                    </div>
                    {!s.done && (
                      <Link
                        to={s.url}
                        data-testid={`playbook-go-${s.key}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-bold hover:bg-muted transition-colors"
                      >
                        Go <ArrowRight size={12} weight="bold" />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
